// frontend/packages/business-console/src/components/media/MediaPickerModal.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import CheckIcon from '@mui/icons-material/Check';

import MediaGrid from './MediaGrid';
import MediaDetailsDrawer from './MediaDetailsDrawer';
import RecentlyUsedRow from './RecentlyUsedRow';
import AltTextRequiredDialog from './AltTextRequiredDialog';

import useCloudinaryUploadWidget from '../../hooks/useCloudinaryUploadWidget';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import {
  createCloudinaryUploadIntent,
  createMediaAssetFromCloudinary,
  listAllMediaAssets,
  markRecentlyUsed,
  updateMediaAsset,
} from '../../services/mediaService';

const normalizeTypeFilter = (tabValue) => {
  if (tabValue === 'video') return 'video';
  if (tabValue === 'image') return 'image';
  return '';
};

export default function MediaPickerModal({ open, onClose, onPick }) {
  const [typeTab, setTypeTab] = useState('all');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [altDialogOpen, setAltDialogOpen] = useState(false);
  const [pendingPick, setPendingPick] = useState(null);

  const {
    ready: widgetReady,
    error: widgetError,
    openWidget,
  } = useCloudinaryUploadWidget();

  const effectiveType = useMemo(() => normalizeTypeFilter(typeTab), [typeTab]);
  // Refetch when the operator stops typing, not on every keystroke.
  const debouncedSearch = useDebouncedValue(search, 300);

  const refresh = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const result = await listAllMediaAssets({
        type: effectiveType,
        search: debouncedSearch,
      });
      setItems(Array.isArray(result?.items) ? result.items : []);
    } catch (e) {
      setError(e?.message || String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveType, debouncedSearch]);

  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open, refresh]);

  const openDetails = useCallback((asset) => {
    setSelected(asset || null);
    setDrawerOpen(true);
  }, []);

  const altRequired = true;

  const doPick = useCallback(
    async (asset) => {
      if (!asset) return;

      const type = String(asset?.type || 'image').toLowerCase();
      const hasAlt = Boolean(String(asset?.altText || '').trim());

      if (altRequired && type === 'image' && !hasAlt) {
        setPendingPick(asset);
        setAltDialogOpen(true);
        return;
      }

      try {
        if (asset?.blobPath) {
          await markRecentlyUsed(asset.blobPath);
        }
      } catch (e) {
        console.warn(
          '[MediaPickerModal] Failed to mark asset as recently used',
          e
        );
      }

      await onPick?.(asset);
    },
    [onPick]
  );

  const handleAltSubmit = useCallback(
    async (altText) => {
      const asset = pendingPick;
      setAltDialogOpen(false);
      setPendingPick(null);
      if (!asset) return;

      try {
        const updated = await updateMediaAsset(asset.id || asset._id, {
          altText,
        });
        await refresh();
        await onPick?.(updated || { ...asset, altText });
      } catch (e) {
        setError(e?.message || String(e));
      }
    },
    [onPick, pendingPick, refresh]
  );

  const handleUpload = useCallback(async () => {
    setError('');

    if (!widgetReady) {
      setError(widgetError || 'Cloudinary widget is not ready yet.');
      return;
    }

    try {
      const intent = await createCloudinaryUploadIntent({
        resourceType: effectiveType || 'image',
        filename: 'upload',
        folder: '',
        tags: [],
      });

      await openWidget({
        intent,
        sources: ['local', 'camera', 'url'],
        onSuccess: async (info) => {
          try {
            const created = await createMediaAssetFromCloudinary(info);
            await refresh();
            if (created?.id) {
              setSelected(created);
            }
          } catch (e) {
            setError(e?.message || String(e));
          }
        },
        onError: (err) => setError(err?.message || String(err)),
      });
    } catch (e) {
      setError(e?.message || String(e));
    }
  }, [effectiveType, openWidget, refresh, widgetError, widgetReady]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Media Library</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {widgetError ? (
              <Alert severity="warning">{widgetError}</Alert>
            ) : null}
            {error ? <Alert severity="error">{error}</Alert> : null}

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ md: 'center' }}
            >
              <Tabs
                value={typeTab}
                onChange={(_e, v) => setTypeTab(v)}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  flex: 1,
                }}
              >
                <Tab value="all" label="All" />
                <Tab value="image" label="Images" />
                <Tab value="video" label="Videos" />
              </Tabs>

              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={handleUpload}
                disabled={!widgetReady}
                sx={{ fontWeight: 900, whiteSpace: 'nowrap' }}
              >
                Upload
              </Button>
            </Stack>

            <TextField
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              fullWidth
            />

            <RecentlyUsedRow onSelect={(asset) => doPick(asset)} />

            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Click to select. Double-click to insert. Use “Details” to edit
              metadata.
            </Typography>

            <MediaGrid
              loading={loading}
              items={items}
              onSelect={(asset) => {
                setSelected(asset);
              }}
              onOpenDetails={(asset) => openDetails(asset)}
              selectedId={selected?.id || selected?._id || ''}
              onDoubleClick={(asset) => doPick(asset)}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="outlined"
            disabled={!selected}
            onClick={() => selected && openDetails(selected)}
            sx={{ fontWeight: 900 }}
          >
            Details
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            disabled={!selected}
            onClick={() => doPick(selected)}
            sx={{ fontWeight: 900 }}
          >
            Select
          </Button>
        </DialogActions>
      </Dialog>

      <MediaDetailsDrawer
        open={drawerOpen}
        asset={selected}
        onClose={() => setDrawerOpen(false)}
        onSave={async (assetId, payload) => {
          try {
            setError('');
            const updated = await updateMediaAsset(assetId, payload);
            await refresh();
            if (updated?.id === selected?.id) {
              setSelected(updated);
            }
            return updated;
          } catch (e) {
            setError(e?.message || String(e));
            return null;
          }
        }}
        onDelete={async () =>
          setError(
            'Delete is disabled in picker. Use the Media page for deletion.'
          )
        }
      />

      <AltTextRequiredDialog
        open={altDialogOpen}
        onClose={() => {
          setAltDialogOpen(false);
          setPendingPick(null);
        }}
        onSubmit={handleAltSubmit}
        assetTitle={pendingPick?.title || pendingPick?.blobPath}
      />
    </>
  );
}
